import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "./api";

/**
 * Datenschutzerklaerung und Impressum.
 *
 * Beides ist Pflicht und beides fehlte, seit das Eigentuemerportal weg ist:
 * dort hingen die Seiten, hier gab es sie nie. Art. 13 DSGVO verlangt die
 * Unterrichtung **im Zeitpunkt der Erhebung**, und erhoben wird die
 * Mailadresse schon auf der Anmeldemaske. Art. 10 LSSI verlangt die
 * Anbieterkennzeichnung **staendig zugaenglich**. Deshalb stehen diese beiden
 * Seiten vor der Anmeldung und nicht dahinter.
 *
 * **Wer hier angesprochen wird.** Nicht der Eigentuemer, sondern der
 * Beschaeftigte. Diese Anwendung ist das Werkzeug des Betriebs, ihre
 * Betroffenen sind die Menschen, die damit stempeln, fahren und berichten.
 * Der Text ist deshalb keine Uebernahme des Portaltextes, sondern die
 * Kurzfassung dessen, was die Mitarbeiterinformation ausfuehrlich sagt.
 *
 * **Warum der Text im Quelltext steht und nicht in i18n.ts.** Er ist kein
 * Bedienelement, sondern Inhalt, und er wird als Ganzes gelesen und geprueft.
 * Wer ihn von einer Gestoria oder einem Abogado gegenlesen laesst, will die
 * Sprachen nebeneinander und nicht ueber zweihundert Zeilen Oberflaechentexte
 * verstreut.
 *
 * **Warum die Firmenangaben aus der Datenbank kommen.** Dieselbe Anwendung
 * laeuft bei jedem Kunden unter dessen Firma, und der Verantwortliche im Sinne
 * der DSGVO ist jeweils dieser Betrieb. Eine Anschrift im Quelltext waere
 * damit bei jedem Kunden ausser einem falsch. Sie kommt deshalb aus den
 * Firmenangaben, derselben Maske wie Telefonnummer und Steuernummer.
 *
 * **Was hier trotzdem fest steht.** Die technischen Empfaenger unter Punkt 6
 * und die Speicherfristen unter Punkt 8 sind Eigenschaften der Anlage und
 * nicht des Kunden: sie gelten fuer jede Installation gleich, weil sie im
 * Quelltext und in den Aufraeumlaeufen so festgelegt sind.
 *
 * Arabisch faellt auf Englisch zurueck. Ein maschinell uebersetzter Rechtstext
 * waere schlechter als ein richtiger in einer Sprache, die der Leser
 * wahrscheinlich auch versteht.
 */

/** Stand des Textes. Aendert sich der Text, aendert sich dieses Datum mit. */
const STAND = "04.09.2026";

export interface Rechtsangaben {
  firmierung: string;
  nif: string;
  anschrift: string;
  register: string;
  email: string;
  telefon: string;
  datenschutzEmail: string;
}

interface Abschnitt {
  titel: string;
  absaetze?: string[];
  punkte?: string[];
  /** Was nach der Aufzaehlung kommt, etwa die Einordnung der ganzen Liste. */
  nachsatz?: string[];
}

const LEER: Rechtsangaben = {
  firmierung: "", nif: "", anschrift: "", register: "",
  email: "", telefon: "", datenschutzEmail: ""
};

export function Rechtsseite({ art }: { art: "datenschutz" | "impressum" }) {
  const { i18n, t } = useTranslation();
  const [angaben, setAngaben] = useState<Rechtsangaben | null>(null);

  useEffect(() => {
    void api.get<Rechtsangaben>("/rechtliches").then(setAngaben).catch(() => setAngaben(LEER));
  }, []);

  const a = angaben ?? LEER;
  const sprache = i18n.language.startsWith("es") ? "es" : i18n.language.startsWith("de") ? "de" : "en";

  const abschnitte = art === "datenschutz"
    ? sprache === "de" ? datenschutzDe(a) : sprache === "es" ? datenschutzEs(a) : datenschutzEn(a)
    : sprache === "de" ? impressumDe(a) : sprache === "es" ? impressumEs(a) : impressumEn(a);

  return (
    <article className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
      <h1 className="text-2xl font-bold">{abschnitte[0].titel}</h1>
      <p className="mt-1 text-sm text-leise">{t("recht.stand", { datum: STAND })}</p>
      {abschnitte.slice(1).map((s) => (
        <section key={s.titel} className="mt-6">
          <h2 className="text-lg font-bold">{s.titel}</h2>
          {s.absaetze?.map((p) => (
            <p key={p} className="mt-2 whitespace-pre-line leading-relaxed">{p}</p>
          ))}
          {s.punkte && (
            <ul className="mt-2 list-disc space-y-1 ps-5 leading-relaxed">
              {s.punkte.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}
          {s.nachsatz?.map((p) => (
            <p key={p} className="mt-2 leading-relaxed">{p}</p>
          ))}
        </section>
      ))}
    </article>
  );
}

/**
 * Die Verweise auf beide Seiten.
 *
 * Sie stehen bewusst unter der Anmeldemaske. Wer sich noch nicht angemeldet
 * hat, gibt gleich seine Mailadresse ein, und genau da muss die Erklaerung
 * erreichbar sein. Gewoehnliche Verweise und kein react-router-Link: die
 * Rechtsseiten liegen ausserhalb der angemeldeten Oberflaeche.
 */
export function Rechtsleiste({ className = "" }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <nav className={`flex flex-wrap justify-center gap-x-6 gap-y-1 text-sm text-leise ${className}`}>
      <a href="/datenschutz" className="underline">{t("recht.datenschutz")}</a>
      <a href="/impressum" className="underline">{t("recht.impressum")}</a>
    </nav>
  );
}

/** Nur ausgeben, was auch gepflegt ist. Eine leere Zeile ist keine Angabe. */
function zeilen(...werte: (string | undefined)[]): string {
  return werte.filter((w) => w && w.trim() !== "").join("\n");
}

// ---------------------------------------------------------------------------
// Deutsch
// ---------------------------------------------------------------------------

function datenschutzDe(a: Rechtsangaben): Abschnitt[] {
  return [
    { titel: "Datenschutzerklärung" },
    {
      titel: "1. Verantwortlicher",
      absaetze: [
        zeilen(a.firmierung, a.anschrift, a.nif && `NIF ${a.nif}`, a.telefon, a.email),
        a.datenschutzEmail
          ? `Fragen zum Datenschutz richten Sie bitte an ${a.datenschutzEmail}.`
          : "Fragen zum Datenschutz richten Sie bitte an die oben genannte Adresse.",
        "Ein Datenschutzbeauftragter ist nicht benannt, weil die gesetzlichen Voraussetzungen dafür nicht vorliegen."
      ]
    },
    {
      titel: "2. Für wen diese Erklärung gilt",
      absaetze: [
        "Diese Anwendung ist das Arbeitswerkzeug des oben genannten Betriebs. Betroffen sind deshalb die Beschäftigten, die damit arbeiten, und die Menschen im Büro, die sie verwalten. Kundendaten und Objektdaten werden hier zwar geführt, aber im Auftrag und auf Grundlage der Verträge mit den Kunden.",
        "Eine ausführliche Fassung dieser Information bekommt jeder Beschäftigte vor der ersten Nutzung ausgehändigt und bestätigt sie mit seiner Unterschrift."
      ]
    },
    {
      titel: "3. Welche Daten verarbeitet werden und warum",
      punkte: [
        "Zugangsdaten: Name, Mailadresse, Telefonnummer, Sprache, Beginn und Ende der Beschäftigung sowie ein verschlüsseltes Passwort. Das Passwort selbst kann auch der Betrieb nicht lesen. Grundlage: Art. 6 Abs. 1 lit. b DSGVO.",
        "Arbeitszeiten mit Beginn, Ende, Pausen und Objekt, dazu jede Korrektur mit Grund und Urheber. Grundlage: Art. 6 Abs. 1 lit. c DSGVO in Verbindung mit Art. 34.9 ET, dem gesetzlichen Arbeitszeitregister.",
        "Ein einzelner Standortpunkt beim Ein- und Ausstempeln, siehe Punkt 4. Grundlage: Art. 6 Abs. 1 lit. f DSGVO, Art. 20.3 ET, Art. 90 LOPDGDD.",
        "Urlaub, Abwesenheit und Krankmeldung. Grundlage: Art. 6 Abs. 1 lit. b und c DSGVO, für Krankmeldungen zusätzlich Art. 9 Abs. 2 lit. b DSGVO. In das Feld für den Grund gehört keine Diagnose.",
        "Fahrzeug, Fahrten und Tankbelege als Betriebsmittel und steuerlicher Nachweis. Grundlage: Art. 6 Abs. 1 lit. f und c DSGVO.",
        "Fotos, die am Objekt aufgenommen werden, als Nachweis der Leistung gegenüber dem Kunden. Grundlage: Art. 6 Abs. 1 lit. b DSGVO.",
        "Anmeldeversuche, Gerätebezeichnung der Sitzung, IP-Adresse und ein Änderungsprotokoll. Grundlage: Art. 6 Abs. 1 lit. f und c sowie Art. 32 DSGVO."
      ]
    },
    {
      titel: "4. Der Standortpunkt, ausdrücklich",
      absaetze: [
        "Erfasst wird ein einzelner Punkt in dem Moment, in dem gestempelt wird: Breite, Länge, Genauigkeit, Zeitpunkt.",
        "Nicht erfasst wird der Weg dazwischen, keine Position während der Arbeit, keine nach Feierabend, keine am Wochenende und keine, wenn die Anwendung geschlossen ist. Eine dauerhafte Ortung gibt es in dieser Anlage nicht.",
        "Der Zweck ist ausschließlich der Nachweis, dass die gestempelte Zeit zum angegebenen Objekt gehört. Nicht die Bewertung einer Leistung, nicht die Messung von Geschwindigkeit, nicht die Kontrolle von Pausen.",
        "Der Standortpunkt wird nach 90 Tagen von der Anlage selbst gelöscht. Gegen seine Erhebung kann nach Art. 21 DSGVO widersprochen werden; das Stempeln selbst bleibt Pflicht, denn das Arbeitszeitregister ist Gesetz, der Punkt daran ist es nicht."
      ]
    },
    {
      titel: "5. Nutzung auf dem eigenen Telefon",
      absaetze: [
        "Die Anwendung läuft im Browser. Sie liest keine Kontakte, keine Nachrichten, keine Fotos aus der Galerie und keine anderen Anwendungen.",
        "Sie fragt nach zwei Berechtigungen, jedes Mal sichtbar und jedes Mal zu bestätigen: die Kamera für ein Foto zum Bericht und den Standort im Moment des Stempelns. Art. 87 LOPDGDD schützt die Privatsphäre auf dem Gerät, und diese Anwendung ist so gebaut, dass sie gar nicht erst dorthin kommt.",
        "Beim Abmelden wird alles gelöscht, was die Anwendung auf dem Gerät abgelegt hatte."
      ]
    },
    {
      titel: "6. Cookies und fremde Dienste",
      absaetze: [
        "Diese Anwendung setzt genau ein Cookie, und zwar für die Anmeldung. Es ist technisch erforderlich, wird nach Art. 22.2 LSSI ohne Einwilligung gesetzt und dient keiner Auswertung und keiner Werbung.",
        "Vom Browser aus wird nichts von fremden Servern nachgeladen: keine Schriften, keine Karten, keine Zählpixel, keine Auswertungsdienste, keine sozialen Netzwerke."
      ]
    },
    {
      titel: "7. Wer die Daten bekommt",
      punkte: [
        "Der Anbieter der Server und des Dateispeichers, Hetzner Online GmbH, Deutschland. Die Daten liegen in Deutschland.",
        "Der Anbieter für den Mailversand, wenn ein Bericht oder eine Einladung hinausgeht.",
        "Gestoría und Steuerberater des Betriebs, soweit es um Lohn, Steuer und Sozialversicherung geht.",
        "Behörden, insbesondere die Arbeitsinspektion, soweit sie es verlangen dürfen.",
        "Anthropic PBC, USA, für die sprachliche Fassung einer Berichtszusammenfassung. Dorthin gehen ausschließlich der Name des Objekts und die festgestellten Punkte, nicht der Name des Beschäftigten.",
        "OpenStreetMap, wenn die Koordinaten einer Anschrift einmalig ermittelt werden. Dorthin geht nur die Anschrift des Objekts."
      ],
      nachsatz: [
        "Alle genannten Stellen sind, soweit sie für den Betrieb tätig werden, als Auftragsverarbeiter nach Art. 28 DSGVO gebunden. Daten werden nicht verkauft und nicht zu Werbezwecken weitergegeben. An Kunden des Betriebs gehen keine personenbezogenen Daten der Beschäftigten."
      ]
    },
    {
      titel: "8. Übermittlung in die USA",
      absaetze: [
        "Die unter Punkt 7 genannte Übermittlung an Anthropic PBC ist die einzige Übermittlung außerhalb der Europäischen Union. Grundlage sind die Standardvertragsklauseln nach Art. 46 Abs. 2 lit. c DSGVO.",
        "Ist diese Funktion in einer Installation nicht eingeschaltet, findet sie nicht statt."
      ]
    },
    {
      titel: "9. Wie lange gespeichert wird",
      punkte: [
        "Standortpunkte: 90 Tage.",
        "Anmeldeversuche und beendete Sitzungen: 90 Tage.",
        "Protokoll versendeter Berichte: 24 Monate.",
        "Änderungsprotokoll: 3 Jahre.",
        "Arbeitszeitregister: 4 Jahre, wie es Art. 34.9 ET verlangt.",
        "Abwesenheiten und Krankmeldungen: in der Regel 4 Jahre.",
        "Stammdaten des Kontos: solange das Arbeitsverhältnis läuft und danach so lange, wie Nachweispflichten bestehen."
      ]
    },
    {
      titel: "10. Rechte der Betroffenen",
      absaetze: [
        "Es bestehen die Rechte auf Auskunft (Art. 15 DSGVO), Berichtigung (Art. 16), Löschung (Art. 17), Einschränkung der Verarbeitung (Art. 18), Datenübertragbarkeit (Art. 20) und Widerspruch gegen Verarbeitungen, die auf einem berechtigten Interesse beruhen (Art. 21).",
        a.datenschutzEmail
          ? `Eine Nachricht an ${a.datenschutzEmail} genügt. Die Ausübung ist kostenlos und darf niemandem zum Nachteil gereichen.`
          : "Eine Nachricht an die oben genannte Adresse genügt. Die Ausübung ist kostenlos und darf niemandem zum Nachteil gereichen.",
        "Eine Beschwerde bei der Aufsichtsbehörde ist jederzeit möglich: Agencia Española de Protección de Datos, C/ Jorge Juan 6, 28001 Madrid, www.aepd.es. Eine vorherige Beschwerde beim Betrieb ist dafür keine Voraussetzung."
      ]
    },
    {
      titel: "11. Keine automatisierte Entscheidung",
      absaetze: [
        "Es werden keine Entscheidungen über Personen allein aufgrund automatisierter Verarbeitung getroffen. Die sprachliche Fassung einer Berichtszusammenfassung ist ein Vorschlag, den ein Mensch prüft und freigibt."
      ]
    },
    {
      titel: "12. Änderungen dieser Erklärung",
      absaetze: [
        "Ändert sich etwas an der Verarbeitung, ändert sich diese Erklärung mit. Das Datum oben zeigt den Stand."
      ]
    }
  ];
}

function impressumDe(a: Rechtsangaben): Abschnitt[] {
  return [
    { titel: "Impressum und Aviso legal" },
    {
      titel: "Anbieter",
      absaetze: [
        zeilen(a.firmierung, a.anschrift, a.nif && `NIF ${a.nif}`, a.telefon, a.email, a.register)
          || "Die Firmenangaben sind in dieser Anlage noch nicht eingetragen."
      ]
    },
    {
      titel: "Zweck dieser Anwendung",
      absaetze: [
        "Diese Anwendung ist das interne Arbeitswerkzeug des oben genannten Betriebs. Sie führt Objekte, Aufträge, Berichte, Arbeitszeiten und Fahrzeuge. Sie steht ausschließlich Beschäftigten offen, ist kein öffentliches Angebot und keine Werbung."
      ]
    },
    {
      titel: "Nutzung",
      absaetze: [
        "Der Zugang ist persönlich. Das Passwort ist nicht weiterzugeben, auch nicht an Kollegen und auch nicht an die Leitung. Niemand aus dem Betrieb wird je danach fragen.",
        "Missbräuchliche Nutzung, insbesondere der Versuch, auf fremde Daten zuzugreifen oder für einen anderen zu stempeln, führt zur sofortigen Sperrung des Zugangs. Für einen anderen zu stempeln ist außerdem eine Fälschung des gesetzlichen Registers."
      ]
    },
    {
      titel: "Rechte an den Inhalten",
      absaetze: [
        "Berichte, Fotos, Texte und die Gestaltung dieser Anwendung sind urheberrechtlich geschützt. Die Anwendung selbst ist lizenziert, nicht verkauft; die darin erfassten Daten gehören dem Betrieb."
      ]
    },
    {
      titel: "Haftung",
      absaetze: [
        "Die Angaben in den Berichten geben den Zustand zum Zeitpunkt des Besuchs wieder. Für den Zustand zu einem späteren Zeitpunkt kann niemand einstehen.",
        "Für die ständige Verfügbarkeit dieser Anwendung wird keine Gewähr übernommen. Wartungsarbeiten und Störungen sind möglich."
      ]
    },
    {
      titel: "Anwendbares Recht",
      absaetze: [
        "Es gilt spanisches Recht. Für Verbraucher bleiben die zwingenden Schutzvorschriften ihres Wohnsitzstaates unberührt."
      ]
    },
    {
      titel: "Datenschutz",
      absaetze: [
        "Wie mit personenbezogenen Daten umgegangen wird, steht in der Datenschutzerklärung."
      ]
    }
  ];
}

// ---------------------------------------------------------------------------
// Englisch
// ---------------------------------------------------------------------------

function datenschutzEn(a: Rechtsangaben): Abschnitt[] {
  return [
    { titel: "Privacy notice" },
    {
      titel: "1. Controller",
      absaetze: [
        zeilen(a.firmierung, a.anschrift, a.nif && `NIF ${a.nif}`, a.telefon, a.email),
        a.datenschutzEmail
          ? `Please address any data protection question to ${a.datenschutzEmail}.`
          : "Please address any data protection question to the contact above.",
        "No data protection officer has been appointed, as the legal conditions for one are not met."
      ]
    },
    {
      titel: "2. Who this notice is for",
      absaetze: [
        "This application is the working tool of the company named above. The people it concerns are therefore the employees who work with it and the office staff who administer it. Customer and property data are held here as well, but on behalf of and under the contracts with those customers.",
        "Every employee receives a detailed version of this information before first use and confirms it by signature."
      ]
    },
    {
      titel: "3. What is processed and why",
      punkte: [
        "Account data: name, email address, telephone number, language, start and end of employment, and an encrypted password. The password itself cannot be read by anyone, including the company. Basis: Art. 6(1)(b) GDPR.",
        "Working time with start, end, breaks and property, plus every correction with reason and author. Basis: Art. 6(1)(c) GDPR together with Art. 34.9 ET, the statutory working time register.",
        "A single location point when clocking in and out, see section 4. Basis: Art. 6(1)(f) GDPR, Art. 20.3 ET, Art. 90 LOPDGDD.",
        "Holiday, absence and sick leave. Basis: Art. 6(1)(b) and (c) GDPR, for sick leave also Art. 9(2)(b) GDPR. The reason field is not for diagnoses.",
        "Vehicle, journeys and fuel receipts as company equipment and tax records. Basis: Art. 6(1)(f) and (c) GDPR.",
        "Photographs taken at a property as evidence of the work towards the customer. Basis: Art. 6(1)(b) GDPR.",
        "Login attempts, device description of the session, IP address and a change log. Basis: Art. 6(1)(f) and (c) and Art. 32 GDPR."
      ]
    },
    {
      titel: "4. The location point, explicitly",
      absaetze: [
        "What is recorded is a single point at the moment of clocking: latitude, longitude, accuracy, time.",
        "What is not recorded is the route in between, no position during work, none after hours, none at weekends and none while the application is closed. This installation has no continuous tracking.",
        "The sole purpose is to show that the recorded time belongs to the stated property. Not to rate performance, not to measure speed, not to monitor breaks.",
        "The location point is deleted by the system after 90 days. It can be objected to under Art. 21 GDPR; clocking itself remains mandatory, because the working time register is law while the point attached to it is not."
      ]
    },
    {
      titel: "5. Use on a personal phone",
      absaetze: [
        "The application runs in the browser. It reads no contacts, no messages, no photos from the gallery and no other applications.",
        "It asks for two permissions, visibly and every time: the camera for a photograph for a report, and the location at the moment of clocking. Art. 87 LOPDGDD protects privacy on the device, and this application is built so that it never reaches it.",
        "On logout, everything the application had stored on the device is deleted."
      ]
    },
    {
      titel: "6. Cookies and third party services",
      absaetze: [
        "This application sets exactly one cookie, for the login. It is technically necessary, is set without consent under Art. 22.2 LSSI, and serves no analytics and no advertising.",
        "Nothing is loaded from third party servers: no fonts, no maps, no tracking pixels, no analytics services, no social networks."
      ]
    },
    {
      titel: "7. Who receives the data",
      punkte: [
        "The provider of the servers and file storage, Hetzner Online GmbH, Germany. The data is held in Germany.",
        "The provider used to send mail, when a report or an invitation goes out.",
        "The company's gestoría and tax adviser, as far as pay, tax and social security are concerned.",
        "Authorities, in particular the labour inspectorate, where they are entitled to demand it.",
        "Anthropic PBC, USA, for the wording of a report summary. Only the property name and the findings are sent there, never an employee's name.",
        "OpenStreetMap, when the coordinates of an address are determined once. Only the property address is sent."
      ],
      nachsatz: [
        "All parties named act, where they work for the company, as processors bound by Art. 28 GDPR. Data is not sold and not passed on for advertising. No personal data of employees is passed to the company's customers."
      ]
    },
    {
      titel: "8. Transfer to the USA",
      absaetze: [
        "The transfer to Anthropic PBC named in section 7 is the only transfer outside the European Union. It is based on the standard contractual clauses under Art. 46(2)(c) GDPR.",
        "Where this function is not switched on in an installation, the transfer does not take place."
      ]
    },
    {
      titel: "9. Retention",
      punkte: [
        "Location points: 90 days.",
        "Login attempts and ended sessions: 90 days.",
        "Log of sent reports: 24 months.",
        "Change log: 3 years.",
        "Working time register: 4 years, as required by Art. 34.9 ET.",
        "Absences and sick leave: as a rule 4 years.",
        "Account master data: for the duration of the employment and afterwards as long as record keeping duties exist."
      ]
    },
    {
      titel: "10. Your rights",
      absaetze: [
        "You have the right of access (Art. 15 GDPR), rectification (Art. 16), erasure (Art. 17), restriction of processing (Art. 18), data portability (Art. 20) and objection to processing based on a legitimate interest (Art. 21).",
        a.datenschutzEmail
          ? `A message to ${a.datenschutzEmail} is enough. Exercising these rights is free of charge and must not put anyone at a disadvantage.`
          : "A message to the contact above is enough. Exercising these rights is free of charge and must not put anyone at a disadvantage.",
        "You may complain to the supervisory authority at any time: Agencia Española de Protección de Datos, C/ Jorge Juan 6, 28001 Madrid, www.aepd.es. A prior complaint to the company is not a precondition."
      ]
    },
    {
      titel: "11. No automated decisions",
      absaetze: [
        "No decisions about a person are taken solely on the basis of automated processing. The wording of a report summary is a proposal that a person reviews and approves."
      ]
    },
    {
      titel: "12. Changes to this notice",
      absaetze: [
        "If the processing changes, this notice changes with it. The date above shows the version."
      ]
    }
  ];
}

function impressumEn(a: Rechtsangaben): Abschnitt[] {
  return [
    { titel: "Legal notice" },
    {
      titel: "Provider",
      absaetze: [
        zeilen(a.firmierung, a.anschrift, a.nif && `NIF ${a.nif}`, a.telefon, a.email, a.register)
          || "The company details have not been entered in this installation yet."
      ]
    },
    {
      titel: "Purpose of this application",
      absaetze: [
        "This application is the internal working tool of the company named above. It holds properties, work orders, reports, working time and vehicles. It is open to employees only, it is not a public offering and not advertising."
      ]
    },
    {
      titel: "Use",
      absaetze: [
        "Access is personal. The password must not be passed on, not to colleagues and not to management. Nobody in the company will ever ask for it.",
        "Misuse, in particular attempting to access other people's data or clocking on behalf of someone else, leads to immediate suspension of access. Clocking for someone else also falsifies the statutory register."
      ]
    },
    {
      titel: "Rights in the content",
      absaetze: [
        "Reports, photographs, texts and the design of this application are protected by copyright. The application itself is licensed, not sold; the data held in it belongs to the company."
      ]
    },
    {
      titel: "Liability",
      absaetze: [
        "The statements in a report describe the condition at the time of the visit. Nobody can vouch for the condition at a later time.",
        "No warranty is given for the constant availability of this application. Maintenance and faults are possible."
      ]
    },
    {
      titel: "Applicable law",
      absaetze: [
        "Spanish law applies. For consumers, the mandatory protective provisions of their country of residence remain unaffected."
      ]
    },
    {
      titel: "Data protection",
      absaetze: [
        "How personal data is handled is set out in the privacy notice."
      ]
    }
  ];
}

// ---------------------------------------------------------------------------
// Spanisch
// ---------------------------------------------------------------------------

function datenschutzEs(a: Rechtsangaben): Abschnitt[] {
  return [
    { titel: "Política de privacidad" },
    {
      titel: "1. Responsable del tratamiento",
      absaetze: [
        zeilen(a.firmierung, a.anschrift, a.nif && `NIF ${a.nif}`, a.telefon, a.email),
        a.datenschutzEmail
          ? `Para cualquier consulta sobre protección de datos, escribe a ${a.datenschutzEmail}.`
          : "Para cualquier consulta sobre protección de datos, escribe a la dirección indicada arriba.",
        "No se ha designado delegado de protección de datos porque no concurren los supuestos legales que lo exigen."
      ]
    },
    {
      titel: "2. A quién se dirige esta información",
      absaetze: [
        "Esta aplicación es la herramienta de trabajo de la empresa indicada arriba. Las personas afectadas son, por tanto, las personas trabajadoras que la utilizan y quienes la administran en la oficina. También se gestionan datos de clientes e inmuebles, pero por cuenta de esos clientes y al amparo de los contratos con ellos.",
        "Cada persona trabajadora recibe una versión detallada de esta información antes del primer uso y la confirma con su firma."
      ]
    },
    {
      titel: "3. Qué datos se tratan y para qué",
      punkte: [
        "Datos de acceso: nombre, correo electrónico, teléfono, idioma, inicio y fin de la relación laboral y una contraseña cifrada. La contraseña no puede leerla nadie, tampoco la empresa. Base: art. 6.1.b RGPD.",
        "Jornada con inicio, fin, pausas e inmueble, y cada corrección con su motivo y su autor. Base: art. 6.1.c RGPD en relación con el art. 34.9 ET, registro horario obligatorio.",
        "Un único punto de localización al fichar la entrada y la salida, véase el apartado 4. Base: art. 6.1.f RGPD, art. 20.3 ET, art. 90 LOPDGDD.",
        "Vacaciones, ausencias y bajas. Base: art. 6.1.b y c RGPD y, para las bajas, art. 9.2.b RGPD. En el campo del motivo no se anota ningún diagnóstico.",
        "Vehículo, desplazamientos y tickets de combustible como medio de trabajo y justificante fiscal. Base: art. 6.1.f y c RGPD.",
        "Fotografías tomadas en el inmueble como prueba del trabajo frente al cliente. Base: art. 6.1.b RGPD.",
        "Intentos de acceso, descripción del dispositivo de la sesión, dirección IP y registro de cambios. Base: art. 6.1.f y c y art. 32 RGPD."
      ]
    },
    {
      titel: "4. El punto de localización, de forma expresa",
      absaetze: [
        "Se registra un único punto en el momento de fichar: latitud, longitud, precisión y hora.",
        "No se registra el recorrido intermedio, ninguna posición durante el trabajo, ninguna después de la jornada, ninguna en fin de semana y ninguna con la aplicación cerrada. Esta instalación no realiza ninguna localización continuada.",
        "La finalidad es únicamente acreditar que el tiempo fichado corresponde al inmueble indicado. No se valora el rendimiento, no se mide la velocidad y no se controlan las pausas.",
        "El punto se borra automáticamente a los 90 días. Cabe oposición conforme al art. 21 RGPD; fichar sigue siendo obligatorio, porque el registro horario es ley, mientras que el punto asociado no lo es."
      ]
    },
    {
      titel: "5. Uso en el teléfono propio",
      absaetze: [
        "La aplicación funciona en el navegador. No lee contactos, ni mensajes, ni fotos de la galería, ni otras aplicaciones.",
        "Solicita dos permisos, de forma visible y cada vez: la cámara para una foto de un informe y la ubicación en el momento de fichar. El art. 87 LOPDGDD protege la intimidad en el dispositivo, y esta aplicación está construida para no llegar a él.",
        "Al cerrar sesión se borra todo lo que la aplicación había guardado en el dispositivo."
      ]
    },
    {
      titel: "6. Cookies y servicios de terceros",
      absaetze: [
        "Esta aplicación instala exactamente una cookie, la de inicio de sesión. Es técnicamente necesaria, se instala sin consentimiento conforme al art. 22.2 LSSI y no sirve para analítica ni para publicidad.",
        "No se carga nada desde servidores ajenos: ni tipografías, ni mapas, ni píxeles de seguimiento, ni servicios de analítica, ni redes sociales."
      ]
    },
    {
      titel: "7. Quién recibe los datos",
      punkte: [
        "El proveedor de los servidores y del almacenamiento de archivos, Hetzner Online GmbH, Alemania. Los datos residen en Alemania.",
        "El proveedor de envío de correo, cuando sale un informe o una invitación.",
        "La gestoría y el asesor fiscal de la empresa, en lo relativo a nómina, impuestos y seguridad social.",
        "Las autoridades, en particular la Inspección de Trabajo, cuando puedan exigirlo.",
        "Anthropic PBC, EE. UU., para la redacción del resumen de un informe. Allí solo se envían el nombre del inmueble y los puntos detectados, nunca el nombre de una persona trabajadora.",
        "OpenStreetMap, cuando se determinan una sola vez las coordenadas de una dirección. Solo se envía la dirección del inmueble."
      ],
      nachsatz: [
        "Todas las partes mencionadas actúan, cuando lo hacen por cuenta de la empresa, como encargados del tratamiento conforme al art. 28 RGPD. Los datos no se venden ni se ceden con fines publicitarios. A los clientes de la empresa no se les facilitan datos personales de las personas trabajadoras."
      ]
    },
    {
      titel: "8. Transferencia a EE. UU.",
      absaetze: [
        "La transferencia a Anthropic PBC indicada en el apartado 7 es la única fuera de la Unión Europea. Se ampara en las cláusulas contractuales tipo del art. 46.2.c RGPD.",
        "Si esta función no está activada en una instalación, la transferencia no se produce."
      ]
    },
    {
      titel: "9. Plazos de conservación",
      punkte: [
        "Puntos de localización: 90 días.",
        "Intentos de acceso y sesiones finalizadas: 90 días.",
        "Registro de informes enviados: 24 meses.",
        "Registro de cambios: 3 años.",
        "Registro horario: 4 años, según exige el art. 34.9 ET.",
        "Ausencias y bajas: por regla general 4 años.",
        "Datos de la cuenta: mientras dure la relación laboral y después mientras subsistan deberes de acreditación."
      ]
    },
    {
      titel: "10. Derechos",
      absaetze: [
        "Cabe ejercer los derechos de acceso (art. 15 RGPD), rectificación (art. 16), supresión (art. 17), limitación del tratamiento (art. 18), portabilidad (art. 20) y oposición a los tratamientos basados en un interés legítimo (art. 21).",
        a.datenschutzEmail
          ? `Basta con un mensaje a ${a.datenschutzEmail}. Su ejercicio es gratuito y no puede perjudicar a nadie.`
          : "Basta con un mensaje a la dirección indicada arriba. Su ejercicio es gratuito y no puede perjudicar a nadie.",
        "También cabe reclamar en cualquier momento ante la autoridad de control: Agencia Española de Protección de Datos, C/ Jorge Juan 6, 28001 Madrid, www.aepd.es. No es requisito previo reclamar antes ante la empresa."
      ]
    },
    {
      titel: "11. Sin decisiones automatizadas",
      absaetze: [
        "No se adoptan decisiones sobre personas basadas únicamente en un tratamiento automatizado. La redacción del resumen de un informe es una propuesta que una persona revisa y aprueba."
      ]
    },
    {
      titel: "12. Cambios en esta información",
      absaetze: [
        "Si cambia el tratamiento, cambia también esta información. La fecha que figura arriba indica la versión."
      ]
    }
  ];
}

function impressumEs(a: Rechtsangaben): Abschnitt[] {
  return [
    { titel: "Aviso legal" },
    {
      titel: "Titular",
      absaetze: [
        zeilen(a.firmierung, a.anschrift, a.nif && `NIF ${a.nif}`, a.telefon, a.email, a.register)
          || "Los datos de la empresa aún no se han introducido en esta instalación."
      ]
    },
    {
      titel: "Finalidad de esta aplicación",
      absaetze: [
        "Esta aplicación es la herramienta interna de trabajo de la empresa indicada arriba. Gestiona inmuebles, órdenes de trabajo, informes, jornada y vehículos. Está reservada a las personas trabajadoras, no constituye una oferta pública ni publicidad."
      ]
    },
    {
      titel: "Uso",
      absaetze: [
        "El acceso es personal. La contraseña no se comparte, ni con compañeros ni con la dirección. Nadie de la empresa la pedirá nunca.",
        "El uso indebido, en particular intentar acceder a datos ajenos o fichar por otra persona, conlleva el bloqueo inmediato del acceso. Fichar por otra persona falsea además el registro horario obligatorio."
      ]
    },
    {
      titel: "Derechos sobre los contenidos",
      absaetze: [
        "Los informes, las fotografías, los textos y el diseño de esta aplicación están protegidos por derechos de autor. La aplicación se licencia, no se vende; los datos registrados en ella pertenecen a la empresa."
      ]
    },
    {
      titel: "Responsabilidad",
      absaetze: [
        "Lo que consta en un informe describe el estado en el momento de la visita. Nadie puede responder del estado en un momento posterior.",
        "No se garantiza la disponibilidad permanente de esta aplicación. Son posibles trabajos de mantenimiento e incidencias."
      ]
    },
    {
      titel: "Legislación aplicable",
      absaetze: [
        "Se aplica el derecho español. Para los consumidores quedan a salvo las normas imperativas de protección de su país de residencia."
      ]
    },
    {
      titel: "Protección de datos",
      absaetze: [
        "El tratamiento de los datos personales se explica en la política de privacidad."
      ]
    }
  ];
}
